/**
 * BackToTop Module - Following guidelines
 * Pure Tailwind, no inline styles, array mapping
 */

import { debugLog } from '../debug/logger.js'

class BackToTop {
	constructor() {
		// Configuration using arrays
		this.CONFIG = {
			SELECTORS: {
				button: '.ui-btt', // Back to top button
				scrollTriggers: '.ui-scroll-to', // All scroll trigger elements
			},
			STATES: {
				button: {
					visible: ['opacity-100', 'pointer-events-auto'],
					hidden: ['opacity-0', 'pointer-events-none'],
				},
			},
			TIMING: {
				SCROLL_DURATION: 500,
				THROTTLE_DELAY: 100,
				SHOW_AFTER: 400, // Show button after scrolling 400px
			},
		}

		// State
		this.isVisible = false
		this.scrollTimer = null

		// Get elements
		this.elements = this.getElements()

		// Initialize
		this.init()
	}

	getElements() {
		const { SELECTORS } = this.CONFIG
		return {
			button: document.querySelector(SELECTORS.button),
			scrollTriggers: document.querySelectorAll(SELECTORS.scrollTriggers),
		}
	}

	init() {
		if (!this.elements.button) {
			debugLog.warn('BackToTop: Button not found with selector', this.CONFIG.SELECTORS.button)
			return
		}

		// Set initial state
		this.hideButton()

		// Bind events
		this.bindEvents()

		// Check initial scroll position immediately
		this.checkScrollPosition()
	}

	bindEvents() {
		// Back to top button click
		if (this.elements.button) {
			this.elements.button.addEventListener('click', e => {
				e.preventDefault()
				this.scrollToTop()
			})
		}

		// Other scroll triggers
		this.elements.scrollTriggers.forEach(trigger => {
			trigger.addEventListener('click', e => {
				e.preventDefault()
				const target = trigger.dataset.target
				this.scrollToTarget(target)
			})
		})

		// Window scroll event (throttled)
		window.addEventListener('scroll', () => this.handleScroll())
	}

	checkScrollPosition() {
		const scrollPos = window.pageYOffset || document.documentElement.scrollTop
		debugLog.info('BackToTop: Initial scroll position', scrollPos)

		if (scrollPos > this.CONFIG.TIMING.SHOW_AFTER) {
			this.showButton()
		} else {
			this.hideButton()
		}
	}

	handleScroll() {
		// Throttle scroll events
		clearTimeout(this.scrollTimer)
		this.scrollTimer = setTimeout(() => {
			const scrollPos = window.pageYOffset || document.documentElement.scrollTop

			if (scrollPos > this.CONFIG.TIMING.SHOW_AFTER) {
				if (!this.isVisible) {
					this.showButton()
				}
			} else {
				if (this.isVisible) {
					this.hideButton()
				}
			}
		}, this.CONFIG.TIMING.THROTTLE_DELAY)
	}

	showButton() {
		if (!this.elements.button) return

		debugLog.info('BackToTop: Showing button')
		const { STATES } = this.CONFIG
		this.swapClasses(this.elements.button, STATES.button.hidden, STATES.button.visible)
		this.isVisible = true
	}

	hideButton() {
		if (!this.elements.button) return

		debugLog.info('BackToTop: Hiding button')
		const { STATES } = this.CONFIG
		this.swapClasses(this.elements.button, STATES.button.visible, STATES.button.hidden)
		this.isVisible = false
	}

	scrollToTop() {
		this.smoothScrollTo(0)
	}

	scrollToTarget(target) {
		// Handle numeric targets (scroll position)
		if (!isNaN(target)) {
			this.smoothScrollTo(parseInt(target))
			return
		}

		// Handle element selectors
		const element = document.querySelector(`.${target}`) || document.querySelector(`#${target}`)
		if (element) {
			const offset = element.getBoundingClientRect().top + window.pageYOffset - 150
			this.smoothScrollTo(offset)
		}
	}

	smoothScrollTo(position) {
		// Use native smooth scroll if supported
		if ('scrollBehavior' in document.documentElement.style) {
			window.scrollTo({
				top: position,
				behavior: 'smooth',
			})
		} else {
			// Fallback for older browsers
			this.animateScroll(position)
		}
	}

	animateScroll(position) {
		const startPosition = window.pageYOffset
		const distance = position - startPosition
		const duration = this.CONFIG.TIMING.SCROLL_DURATION
		let start = null

		const animation = currentTime => {
			if (start === null) start = currentTime
			const timeElapsed = currentTime - start
			const progress = Math.min(timeElapsed / duration, 1)

			// Easing function
			const ease = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress

			window.scrollTo(0, startPosition + distance * ease)

			if (timeElapsed < duration) {
				requestAnimationFrame(animation)
			}
		}

		requestAnimationFrame(animation)
	}

	/**
	 * Helper to swap class arrays cleanly
	 */
	swapClasses(element, remove, add) {
		if (!element) return
		element.classList.remove(...remove)
		element.classList.add(...add)
	}
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new BackToTop())
} else {
	new BackToTop()
}

// Export for use
window.BackToTop = BackToTop
